import React from "react";
import PropTypes from "prop-types";
import { Navigate, Routes, Route } from "react-router-dom";
import { featureEnabled, featureEquals } from "byzantine/src/Feature";
import { modules } from "byzantine";
import { useIsBusinessUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";

import { WireTransfer } from "../../src";
import { AccountContextProvider } from "../contexts/AccountContext";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import BillPay from "./BillPay";
import M2MTransfer from "./M2MTransfer";
import JhaZelle from "./JhaZelle";
import GenericZelle, { ZelleUrl } from "./GenericZelle";
import CustomizableTransferMenu from "./CustomizableTransferMenu";
import TransferTilesMenu from "./TransferTilesMenu";
import LoanPaymentRouter from "./loans/LoanPaymentRouter";
import ScrollToTopOnRouteChange from "../ScrollToTopOnRouteChange";
import { SudoProvider } from "../SudoContext";
import RequestOTP from "../SudoContext/RequestOTP";
import SubmitOTP from "../SudoContext/SubmitOTP";
import WireTransferActionReview from "./wires/review/WireTransferActionReview";
import ACHPaymentRoutes, { ACH_PAYMENT_ROUTE } from "./ach/ACHPaymentRoutes";
import InvoiceRoutes, { INVOICE_ROUTE } from "./invoice/InvoiceRoutes";
import TransferRoutes, { TRANSFERS_ROUTE } from "./funds/TransferRoutes";

const TransferPageContainer = ({ accounts, limitsJson, features }) => {
  const limits = JSON.parse(limitsJson);
  const { showWireTransferOptionInMenu: canNavigateToWiresFlow } =
    modules.wireTemplates.useWirePermissions();
  const shouldShowSingleACHPaymentFlow =
    modules.achPayments.useShouldShowSingleACHPaymentFlow();

  const isBusinessUser = useIsBusinessUser();

  return (
    <AccountContextProvider accounts={[...accounts]}>
      <InstitutionSettingsContextProvider>
        <SudoProvider basename="transfer">
          <ScrollToTopOnRouteChange />
          <Routes>
            {featureEnabled(features, {
              or: ["internal_transfers", "ach"],
            }) && (
              <Route
                path={`${TRANSFERS_ROUTE}/*`}
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-menu">
                      <TransferRoutes limits={limits} />
                    </div>
                  </div>
                }
              />
            )}
            {featureEnabled(features, { or: ["bill_pay"] }) && (
              <Route
                path="bill_pay"
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-page">
                      <BillPay />
                    </div>
                  </div>
                }
              />
            )}
            {featureEquals(features, "p2p", "m2m") && (
              <Route
                path="m2m"
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-page">
                      <M2MTransfer />
                    </div>
                  </div>
                }
              />
            )}
            <Route path="sudo/request-otp" element={<RequestOTP />} />
            <Route path="sudo/submit-otp" element={<SubmitOTP />} />
            <Route path="wires/review" element={<WireTransferActionReview />} />
            {canNavigateToWiresFlow && (
              <Route
                path="wires/*"
                element={<WireTransfer limits={limits} />}
              />
            )}
            {featureEnabled(features, { or: ["zelle"] }) && (
              <Route
                path="zelle"
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-page">
                      {(featureEquals(features, "zelle", true) ||
                        featureEquals(features, "zelle", "jha")) && (
                        <JhaZelle />
                      )}
                      {featureEquals(features, "zelle", "fis") && (
                        <GenericZelle url={ZelleUrl.FIS} />
                      )}
                      {featureEquals(features, "zelle", "fiserv") && (
                        <GenericZelle url={ZelleUrl.FISERV} />
                      )}
                    </div>
                  </div>
                }
              />
            )}
            {featureEnabled(features, {
              or: ["internal_transfers", "ach"],
            }) &&
              featureEnabled(features, {
                or: [
                  "loan_principal_payment",
                  "loan_payment_by_card",
                  "enable_loan_payments_web",
                ],
              }) && (
                <Route
                  path="loans/*"
                  element={
                    <div className="transfer-page-layout">
                      <div className="transfer-page loan-payment">
                        <LoanPaymentRouter limits={limits} />
                      </div>
                    </div>
                  }
                />
              )}
            {featureEnabled(features, {
              or: ["internal_transfers", "ach"],
            }) &&
            featureEnabled(features, {
              or: ["loan_principal_payment", "loan_payment_by_card"],
              not: ["enable_loan_payments_web"],
            }) ? (
              <Route
                path="loans/*"
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-page loan-payment">
                      <LoanPaymentRouter limits={limits} legacy />
                    </div>
                  </div>
                }
              />
            ) : null}
            {shouldShowSingleACHPaymentFlow ? (
              <Route
                path={`${ACH_PAYMENT_ROUTE}/*`}
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-menu">
                      <ACHPaymentRoutes limits={limits} />
                    </div>
                  </div>
                }
              />
            ) : null}
            {features.invoice_pay ? (
              <Route path={`${INVOICE_ROUTE}/*`} element={<InvoiceRoutes />} />
            ) : null}
            {isBusinessUser ||
            featureEnabled(features, {
              or: ["transfer_tiles_page_consumer"],
            }) ? (
              <Route
                index
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-menu">
                      <TransferTilesMenu />
                    </div>
                  </div>
                }
              />
            ) : (
              <Route
                index
                element={
                  <div className="transfer-page-layout">
                    <div className="transfer-menu">
                      <CustomizableTransferMenu />
                    </div>
                  </div>
                }
              />
            )}
            <Route path="*" element={<Navigate to="/transfer" replace />} />
          </Routes>
        </SudoProvider>
      </InstitutionSettingsContextProvider>
    </AccountContextProvider>
  );
};

TransferPageContainer.propTypes = {
  accounts: PropTypes.array,
  institution: PropTypes.object,
  payees: PropTypes.array,
  limitsJson: PropTypes.string,
  features: PropTypes.object,
};

export default TransferPageContainer;
